import { useCallback, useEffect, useMemo, useState } from 'react';
import { authService } from '@watchlist/shared';
import { useAuth } from '../contexts/AuthContext';
import { useUserMovieData } from '../contexts/UserMovieDataContext';
import { useUserWatchlistData } from '../contexts/UserWatchlistDataContext';
import { useToast } from '../components/utils/ToastProvider';
import { TMDbMovie } from '../services/tmdbApi';
import { OnboardingData } from '../components/onboarding/OnboardingFlow';

type MoviePreferences = OnboardingData['moviePreferences'];

const EMPTY_PREFS: MoviePreferences = {
  watchedMovies: [],
  watchlistMovies: [],
  dislikedMovies: [],
  ratings: {},
  movieDetails: {},
};

interface QuickTakesController {
  isOpen: boolean;
  open: () => void;
  preferredLanguages: string[];
  /**
   * Props to spread onto MovieTasteStepV2. The dialog is render-controlled by
   * `isOpen` (so callers do `{isOpen && <MovieTasteStepV2 {...dialogProps} />}`).
   */
  dialogProps: {
    movies: TMDbMovie[];
    preferences: MoviePreferences;
    selectedLanguages: string[];
    onPreferencesUpdate: (p: MoviePreferences) => void;
    onComplete: () => void;
    onBack: () => void;
    excludeMovieIds: Set<string>;
    onMoviesSeen: (ids: string[]) => void;
  };
}

/**
 * Owns the state needed to mount MovieTasteStepV2 as the Quick Takes flow
 * outside of onboarding. Lifted out of the now-deleted WatchlistAppShell so
 * the Featured "Quick Takes" card on Search → Explore can open it.
 */
export function useQuickTakes(): QuickTakesController {
  const { user } = useAuth();
  const { addToWatchlist, markAsWatched, refreshAllMovieData, getWatchlistMovieIds, getWatchedMovieIds } =
    useUserMovieData();
  const { refreshData } = useUserWatchlistData();
  const { showSuccess, showError } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [preferences, setPreferences] = useState<MoviePreferences>(EMPTY_PREFS);
  const [preferredLanguages, setPreferredLanguages] = useState<string[]>(['en']);
  const [seenQuickTakesMovies, setSeenQuickTakesMovies] = useState<string[]>([]);

  // Hydrate user preferences once we have a signed-in user.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const prefs = await authService.getUserPreferences(user);
        if (cancelled) return;
        setSeenQuickTakesMovies(prefs?.seenQuickTakesMovies || []);
        setPreferredLanguages(prefs?.preferredLanguages || ['en']);
      } catch (err) {
        console.error('useQuickTakes: failed to load preferences', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const excludeMovieIds = useMemo(() => {
    const wl = getWatchlistMovieIds();
    const watched = getWatchedMovieIds();
    return new Set([...wl, ...watched, ...seenQuickTakesMovies]);
  }, [getWatchlistMovieIds, getWatchedMovieIds, seenQuickTakesMovies]);

  const onMoviesSeen = useCallback(
    async (movieIds: string[]) => {
      if (!user || movieIds.length === 0) return;
      try {
        const merged = Array.from(new Set([...seenQuickTakesMovies, ...movieIds]));
        await authService.updateUserPreferences(user.uid, {
          seenQuickTakesMovies: merged,
        } as any);
        setSeenQuickTakesMovies(merged);
      } catch (err) {
        console.error('useQuickTakes: failed to save seen movies', err);
      }
    },
    [user, seenQuickTakesMovies],
  );

  /** Persist watched/watchlist movies the user marked while in the flow. */
  const save = useCallback(
    async (showSuccessMsg: boolean) => {
      try {
        for (const movieId of preferences.watchedMovies) {
          const rating = preferences.ratings[movieId];
          const detail = preferences.movieDetails[movieId];
          if (!detail) continue;
          try {
            await markAsWatched(
              movieId,
              detail.title,
              rating,
              undefined,
              false,
              detail.poster,
              detail.year,
            );
          } catch (err) {
            console.error('useQuickTakes: markAsWatched failed', movieId, err);
          }
        }
        for (const movieId of preferences.watchlistMovies) {
          const detail = preferences.movieDetails[movieId];
          if (!detail) continue;
          try {
            await addToWatchlist(movieId, detail.title, detail.poster, detail.year);
          } catch (err) {
            console.error('useQuickTakes: addToWatchlist failed', movieId, err);
          }
        }

        await refreshAllMovieData();
        await refreshData();
        // Force a fresh poster fetch — the old shell cleared this; preserve parity.
        localStorage.removeItem('watchlist_posters_cache_v1');

        const total = preferences.watchedMovies.length + preferences.watchlistMovies.length;
        if (showSuccessMsg && total > 0) {
          showSuccess(`Added ${total} ${total === 1 ? 'movie' : 'movies'}!`);
        }
      } catch (err) {
        console.error('useQuickTakes: save failed', err);
        if (showSuccessMsg) showError('Failed to save some movies');
      } finally {
        setPreferences(EMPTY_PREFS);
      }
    },
    [preferences, markAsWatched, addToWatchlist, refreshAllMovieData, refreshData, showSuccess, showError],
  );

  const onComplete = useCallback(() => {
    setIsOpen(false);
    void save(true);
  }, [save]);

  const onBack = useCallback(() => {
    setIsOpen(false);
    void save(false);
  }, [save]);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  return {
    isOpen,
    open,
    preferredLanguages,
    dialogProps: {
      movies: [],
      preferences,
      selectedLanguages: preferredLanguages,
      onPreferencesUpdate: setPreferences,
      onComplete,
      onBack,
      excludeMovieIds,
      onMoviesSeen,
    },
  };
}
